import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Search,
  MoreVertical,
  Eye,
  CheckCircle,
  XCircle,
  Trash2,
  MapPin,
  Calendar,
  User,
  X,
  Download,
  Home,
  Tag,
  Loader
} from 'lucide-react';
import { formatDate, formatPrice, exportToCSV } from '../utils/helpers';
import { offersService } from '../services';
import { useToast } from '../context/ToastContext';
import DropdownMenu from '../components/DropdownMenu';
import './OffersManagementPage.css';

const OffersManagementPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [offerToReject, setOfferToReject] = useState(null);

  // API state
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, total: 1, count: 0 });
  const currentPage = pagination.current;

  // Fetch offers from API
  const fetchOffers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: 20
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (statusFilter !== 'all') {
        params.approvalStatus = statusFilter;
      }

      if (typeFilter !== 'all') {
        params.propertyType = typeFilter;
      }

      if (sortBy === 'oldest') {
        params.sortBy = 'createdAt';
        params.sortOrder = 'asc';
      } else {
        params.sortBy = 'createdAt';
        params.sortOrder = 'desc';
      }

      const response = await offersService.getAll(params);

      if (response.success) {
        setOffers(response.data.offers || []);
        setStats(response.data.stats || { total: 0, pending: 0, approved: 0, rejected: 0 });
        setPagination(response.data.pagination || { current: 1, total: 1, count: 0 });
      } else {
        setError(response.message || t('errors.fetchFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, typeFilter, sortBy, currentPage, t]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleApprove = useCallback(async (offerId) => {
    try {
      const response = await offersService.approve(offerId);
      if (response.success) {
        fetchOffers();
        if (showOfferModal) setShowOfferModal(false);
      } else {
        toast.error(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.updateFailed'));
    }
    setActiveMenu(null);
  }, [fetchOffers, showOfferModal, t, toast]);

  const handleRejectClick = useCallback((offer) => {
    setOfferToReject(offer);
    setRejectReason('');
    setShowRejectModal(true);
    setActiveMenu(null);
  }, []);

  const handleRejectConfirm = useCallback(async () => {
    if (!offerToReject) return;
    try {
      const response = await offersService.reject(offerToReject._id, rejectReason);
      if (response.success) {
        fetchOffers();
        setShowRejectModal(false);
        setOfferToReject(null);
        if (showOfferModal) setShowOfferModal(false);
      } else {
        toast.error(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.updateFailed'));
    }
  }, [offerToReject, rejectReason, fetchOffers, showOfferModal, t, toast]);

  const handleDelete = useCallback(async (offerId) => {
    if (window.confirm(t('offers.confirmDelete'))) {
      try {
        const response = await offersService.delete(offerId);
        if (response.success) {
          fetchOffers();
        } else {
          toast.error(response.message || t('errors.deleteFailed'));
        }
      } catch (err) {
        toast.error(err.message || t('errors.deleteFailed'));
      }
    }
    setActiveMenu(null);
  }, [fetchOffers, t, toast]);

  const handleViewOffer = useCallback((offer) => {
    setSelectedOffer(offer);
    setShowOfferModal(true);
    setActiveMenu(null);
  }, []);

  const handleExport = useCallback(() => {
    const propertyTypes = {
      villa: 'فيلا',
      apartment: 'شقة',
      land: 'أرض',
      building: 'عمارة',
      commercial: 'تجاري'
    };
    
    const columns = [
      { key: 'title', label: 'العنوان' },
      { key: 'propertyType', label: 'نوع العقار', formatter: (v) => propertyTypes[v] || v },
      { key: 'price', label: 'السعر', formatter: formatPrice },
      { key: 'city', label: 'المدينة' },
      { key: 'district', label: 'الحي' },
      { key: 'area', label: 'المساحة' },
      { key: 'approvalStatus', label: 'الحالة' },
      { key: 'user.name', label: 'المسوق' },
      { key: 'createdAt', label: 'تاريخ الإضافة', formatter: formatDate }
    ];
    
    if (offers.length === 0) {
      toast.warning('لا توجد بيانات للتصدير');
      return;
    }
    
    const success = exportToCSV(offers, columns, 'offers');
    if (success) {
      toast.success(t('common.exportSuccess'));
    } else {
      toast.error(t('errors.exportFailed'));
    }
  }, [offers, t, toast]);

  const getStatusBadge = (status) => {
    const badges = {
      approved: { label: t('status.approved'), class: 'badge-success' },
      pending: { label: t('status.pending'), class: 'badge-warning' },
      rejected: { label: t('status.rejected'), class: 'badge-danger' }
    };
    return badges[status] || badges.pending;
  };

  const getPropertyType = (type) => {
    const types = {
      villa: t('offers.villa'),
      apartment: t('offers.apartment'),
      land: t('offers.land'),
      building: t('offers.building'),
      duplex: t('offers.duplex'),
      commercial: t('offers.commercial')
    };
    return types[type] || type;
  };

  // Map API offer to display format
  const mapOffer = (offer) => ({
    ...offer,
    id: offer._id,
    status: offer.approvalStatus || 'pending',
    type: offer.propertyType,
    marketer: offer.user || { name: 'غير معروف' }
  });

  const mappedOffers = offers.map(mapOffer);

  return (
    <div className="offers-management-page">
      <div className="page-header">
        <div className="header-title">
          <Building2 size={28} />
          <div>
            <h1>{t('offers.management')}</h1>
            <span className="count">{stats.total} {t('offers.offer')}</span>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={handleExport}>
            <Download size={18} />
            {t('common.export')}
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div className={`quick-stat ${statusFilter === 'all' ? 'selected' : ''}`} onClick={() => setStatusFilter('all')}>
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">{t('offers.total')}</span>
        </div>
        <div className={`quick-stat pending ${statusFilter === 'pending' ? 'selected' : ''}`} onClick={() => setStatusFilter('pending')}>
          <span className="stat-number">{stats.pending}</span>
          <span className="stat-label">{t('status.pending')}</span>
        </div>
        <div className={`quick-stat approved ${statusFilter === 'approved' ? 'selected' : ''}`} onClick={() => setStatusFilter('approved')}>
          <span className="stat-number">{stats.approved}</span>
          <span className="stat-label">{t('status.approved')}</span>
        </div>
        <div className={`quick-stat rejected ${statusFilter === 'rejected' ? 'selected' : ''}`} onClick={() => setStatusFilter('rejected')}>
          <span className="stat-number">{stats.rejected}</span>
          <span className="stat-label">{t('status.rejected')}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="data-section">
        <div className="filters-row">
          <div className="search-wrapper">
            <Search size={20} />
            <input
              type="text"
              className="form-input"
              placeholder={t('offers.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t('common.allStatus')}</option>
            <option value="pending">{t('status.pending')}</option>
            <option value="approved">{t('status.approved')}</option>
            <option value="rejected">{t('status.rejected')}</option>
          </select>
          <select
            className="filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">{t('common.allTypes')}</option>
            <option value="villa">{t('offers.villa')}</option>
            <option value="apartment">{t('offers.apartment')}</option>
            <option value="land">{t('offers.land')}</option>
            <option value="building">{t('offers.building')}</option>
            <option value="duplex">{t('offers.duplex')}</option>
          </select>
          <select
            className="filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="newest">الأحدث أولاً</option>
            <option value="oldest">الأقدم أولاً</option>
          </select>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <Loader size={32} className="spin" />
            <p>{t('common.loading')}</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-state">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchOffers}>
              {t('common.retry')}
            </button>
          </div>
        )}

        {/* Offers Table */}
        {!loading && !error && (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('offers.offer')}</th>
                  <th>{t('offers.location')}</th>
                  <th>{t('offers.price')}</th>
                  <th>{t('offers.marketer')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {mappedOffers.map((offer) => {
                  const statusBadge = getStatusBadge(offer.status);
                  return (
                    <tr key={offer.id}>
                      <td>
                        <div className="offer-cell">
                          <div className="offer-icon">
                            <Home size={20} />
                          </div>
                          <div className="offer-info">
                            <span className="offer-title">{offer.title}</span>
                            <div className="offer-meta">
                              <span className="offer-type">
                                <Tag size={12} />
                                {getPropertyType(offer.type)}
                              </span>
                              <span className="offer-area">{offer.area} م²</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="location-cell">
                          <MapPin size={14} />
                          <span>{offer.city} - {offer.district}</span>
                        </div>
                      </td>
                      <td>
                        <div className="price-cell">
                          <span className="price">{formatPrice(offer.price)}</span>
                          <span className="currency">{t('common.sar')}</span>
                        </div>
                      </td>
                      <td>
                        <div className="marketer-cell">
                          <User size={14} />
                          <span>{offer.marketer?.name || 'غير معروف'}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${statusBadge.class}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td>
                        <div className="date-cell">
                          <Calendar size={14} />
                          {formatDate(offer.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <DropdownMenu
                            isOpen={activeMenu === offer.id}
                            onClose={() => setActiveMenu(null)}
                            trigger={
                              <button
                                className="action-btn-icon"
                                onClick={() => setActiveMenu(activeMenu === offer.id ? null : offer.id)}
                              >
                                <MoreVertical size={18} />
                              </button>
                            }
                          >
                            <button onClick={() => { handleViewOffer(offer); setActiveMenu(null); }}>
                              <Eye size={16} />
                              {t('common.view')}
                            </button>
                            {offer.status === 'pending' && (
                              <>
                                <button onClick={() => { handleApprove(offer.id); setActiveMenu(null); }}>
                                  <CheckCircle size={16} />
                                  {t('offers.approve')}
                                </button>
                                <button className="danger" onClick={() => { handleRejectClick(offer); setActiveMenu(null); }}>
                                  <XCircle size={16} />
                                  {t('offers.reject')}
                                </button>
                              </>
                            )}
                            <button className="danger" onClick={() => { handleDelete(offer.id); setActiveMenu(null); }}>
                              <Trash2 size={16} />
                              {t('common.delete')}
                            </button>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && mappedOffers.length === 0 && (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>{t('offers.noOffers')}</h3>
            <p>{t('offers.noOffersDescription')}</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && pagination.total > 1 && (
          <div className="pagination">
            <button
              className="btn btn-outline"
              disabled={pagination.current === 1}
              onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
            >
              {t('common.previous')}
            </button>
            <span className="pagination-info">
              {t('common.page')} {pagination.current} {t('common.of')} {pagination.total}
            </span>
            <button
              className="btn btn-outline"
              disabled={pagination.current === pagination.total}
              onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      {/* Offer Details Modal */}
      {showOfferModal && selectedOffer && (
        <div className="modal-overlay" onClick={() => setShowOfferModal(false)}>
          <div className="modal offer-modal large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('offers.offerDetails')}</h2>
              <button className="modal-close" onClick={() => setShowOfferModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="offer-header-detail">
                <h3>{selectedOffer.title || 'بدون عنوان'}</h3>
                <div className="badges-row">
                  <span className={`badge ${getStatusBadge(selectedOffer.status).class}`}>
                    {getStatusBadge(selectedOffer.status).label}
                  </span>
                  <span className={`badge ${selectedOffer.offerType === 'sale' ? 'badge-primary' : 'badge-purple'}`}>
                    {selectedOffer.offerType === 'sale' ? 'بيع' : 'إيجار'}
                  </span>
                </div>
              </div>

              {/* Basic Details */}
              <div className="offer-details-grid">
                <div className="detail-item">
                  <span className="detail-label">{t('offers.type')}</span>
                  <span className="detail-value">{getPropertyType(selectedOffer.type || selectedOffer.propertyType)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('offers.price')}</span>
                  <span className="detail-value">{formatPrice(selectedOffer.price)} {t('common.sar')}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('offers.location')}</span>
                  <span className="detail-value">{selectedOffer.city} - {selectedOffer.district}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('offers.area')}</span>
                  <span className="detail-value">{selectedOffer.area} {t('common.sqm')}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('offers.rooms')}</span>
                  <span className="detail-value">{selectedOffer.rooms || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">الحمامات</span>
                  <span className="detail-value">{selectedOffer.bathrooms || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">الصالات</span>
                  <span className="detail-value">{selectedOffer.lounges || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">المطابخ</span>
                  <span className="detail-value">{selectedOffer.kitchens || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('offers.views')}</span>
                  <span className="detail-value">{selectedOffer.viewCount || 0}</span>
                </div>
              </div>

              {/* Property Details */}
              <div className="section-divider">
                <h4>تفاصيل العقار</h4>
              </div>
              <div className="offer-details-grid">
                {selectedOffer.age > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">عمر العقار</span>
                    <span className="detail-value">{selectedOffer.age} سنة</span>
                  </div>
                )}
                {selectedOffer.floors > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">الأدوار</span>
                    <span className="detail-value">{selectedOffer.floors}</span>
                  </div>
                )}
                {selectedOffer.streetWidth > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">عرض الشارع</span>
                    <span className="detail-value">{selectedOffer.streetWidth} م</span>
                  </div>
                )}
                {selectedOffer.direction && (
                  <div className="detail-item">
                    <span className="detail-label">الواجهة</span>
                    <span className="detail-value">
                      {selectedOffer.direction === 'north' ? 'شمال' :
                       selectedOffer.direction === 'south' ? 'جنوب' :
                       selectedOffer.direction === 'east' ? 'شرق' :
                       selectedOffer.direction === 'west' ? 'غرب' :
                       selectedOffer.direction === 'northeast' ? 'شمال شرقي' :
                       selectedOffer.direction === 'northwest' ? 'شمال غربي' :
                       selectedOffer.direction === 'southeast' ? 'جنوب شرقي' :
                       selectedOffer.direction === 'southwest' ? 'جنوب غربي' : selectedOffer.direction}
                    </span>
                  </div>
                )}
                {selectedOffer.offerType === 'sale' && (
                  <>
                    <div className="detail-item">
                      <span className="detail-label">نوع الصك</span>
                      <span className="detail-value">{selectedOffer.deedType === 'free' ? 'حر' : selectedOffer.deedType === 'mortgaged' ? 'مرهون' : '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">صفة العرض</span>
                      <span className="detail-value">
                        {selectedOffer.offerNature === 'owner' ? 'المالك' :
                         selectedOffer.offerNature === 'direct' ? 'مباشر مع المالك' :
                         selectedOffer.offerNature === 'withDirect' ? 'مع المباشر' :
                         selectedOffer.offerNature === 'with_direct' ? 'مع المباشر' :
                         selectedOffer.offerNature === 'multiple' ? 'معي 2 او اكثر' :
                         selectedOffer.offerNature === 'two_levels' ? 'معي ٢' :
                         selectedOffer.offerNature === 'three_levels' ? 'معي ٣' :
                         selectedOffer.offerNature === 'indirect' ? 'غير مباشر' : '-'}
                      </span>
                    </div>
                  </>
                )}
                {selectedOffer.offerType === 'rent' && (
                  <>
                    <div className="detail-item">
                      <span className="detail-label">يقبل دفعات</span>
                      <span className="detail-value">{selectedOffer.acceptsPayments === 'yes' ? 'نعم' : 'لا'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">الكهرباء</span>
                      <span className="detail-value">{selectedOffer.electricity === 'independent' ? 'مستقل' : 'مشترك'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">الماء</span>
                      <span className="detail-value">{selectedOffer.water === 'independent' ? 'مستقل' : 'مشترك'}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Owner Info */}
              {(selectedOffer.ownerName || selectedOffer.ownerPhone) && (
                <>
                  <div className="section-divider">
                    <h4>معلومات صاحب العرض</h4>
                  </div>
                  <div className="owner-info-box">
                    {selectedOffer.ownerName && (
                      <div className="owner-detail">
                        <span className="label">الاسم:</span>
                        <span className="value">{selectedOffer.ownerName}</span>
                      </div>
                    )}
                    {selectedOffer.ownerPhone && (
                      <div className="owner-detail">
                        <span className="label">الهاتف:</span>
                        <span className="value" dir="ltr">{selectedOffer.ownerPhone}</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Comment */}
              {selectedOffer.comment && (
                <>
                  <div className="section-divider">
                    <h4>تعليق / ملاحظات</h4>
                  </div>
                  <div className="comment-box">
                    <p>{selectedOffer.comment}</p>
                  </div>
                </>
              )}

              {/* Description */}
              {selectedOffer.description && (
                <>
                  <div className="section-divider">
                    <h4>الوصف</h4>
                  </div>
                  <div className="description-box">
                    <p>{selectedOffer.description}</p>
                  </div>
                </>
              )}

              {/* Marketer Info */}
              <div className="section-divider">
                <h4>{t('offers.marketerInfo')}</h4>
              </div>
              <div className="marketer-section">
                <div className="marketer-info">
                  <div className="marketer-avatar">
                    {selectedOffer.marketer?.name?.charAt(0) || '?'}
                  </div>
                  <span>{selectedOffer.marketer?.name || 'غير معروف'}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {selectedOffer.status === 'pending' && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => handleApprove(selectedOffer.id)}
                  >
                    <CheckCircle size={18} />
                    {t('offers.approve')}
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleRejectClick(selectedOffer)}
                  >
                    <XCircle size={18} />
                    {t('offers.reject')}
                  </button>
                </>
              )}
              <button className="btn btn-outline" onClick={() => setShowOfferModal(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('offers.rejectOffer')}</h2>
              <button className="modal-close" onClick={() => setShowRejectModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('offers.rejectReason')}</label>
                <textarea
                  className="form-textarea"
                  rows={4}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t('offers.rejectReasonPlaceholder')}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-danger" onClick={handleRejectConfirm}>
                <XCircle size={18} />
                {t('offers.confirmReject')}
              </button>
              <button className="btn btn-outline" onClick={() => setShowRejectModal(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OffersManagementPage;
